import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Hero } from "../sections/Hero";
import { GoogleReviews } from "../sections/GoogleReviews";
import { SERVICES } from "../lib/site-data";

export default function HomePage() {
  return (
    <main data-testid="home-page">
      <Hero />

      {/* What we do */}
      <section className="py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-10 lg:gap-16 mb-12 lg:mb-16">
            <div className="lg:col-span-5">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                  What we do
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
                One team.
                <br />
                <span className="text-[#D4AF37]">Every finish.</span>
              </h2>
            </div>
            <div className="lg:col-span-7 lg:pt-2">
              <p className="text-base sm:text-[17px] text-slate-300 leading-[1.75] max-w-xl">
                From a single hardwood install to a full home remodel, DN
                Flooring handles every trade in-house — so the craft, the
                schedule, and the finish all come from the same hands.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {SERVICES.map((s, i) => (
              <motion.div
                key={s.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                className="group relative rounded-lg overflow-hidden border border-white/5 bg-[#2a3744]"
                data-testid={`home-service-${s.title.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.title}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.06]"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#1f2935] via-[#1f2935]/40 to-transparent" />
                </div>
                <div className="absolute inset-x-0 bottom-0 p-5">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] mb-1.5">
                    0{i + 1}
                  </div>
                  <h3 className="font-serif text-lg text-white leading-tight">
                    {s.title}
                  </h3>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-10">
            <Link
              to="/services"
              className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-[#D4AF37] hover:text-white transition-colors"
              data-testid="home-services-link"
            >
              Explore all services <ArrowRight size={12} strokeWidth={2} />
            </Link>
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="relative py-20 lg:py-28 border-y border-white/5 bg-[#171f29]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-10">
          <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            <div className="lg:col-span-8">
              <div className="flex items-center gap-3 mb-4">
                <span className="h-px w-8 bg-[#D4AF37]" />
                <span className="text-[11px] uppercase tracking-[0.28em] text-[#D4AF37]">
                  Ready to start
                </span>
              </div>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
                Let's design
                <br />
                <span className="text-[#D4AF37]">your next space.</span>
              </h2>
              <p className="mt-5 text-base text-slate-300 leading-[1.7] max-w-lg">
                Free quote — usually back to you the same day. No pressure,
                just clear pricing and an honest plan.
              </p>
            </div>
            <div className="lg:col-span-4 flex lg:justify-end">
              <Link
                to="/quote"
                className="inline-flex items-center gap-2 px-7 py-4 rounded-full bg-[#D4AF37] text-[#1f2935] font-semibold text-[12px] uppercase tracking-[0.22em] hover:bg-[#B48600] transition-colors"
                data-testid="home-cta-quote"
              >
                Get a Quote <ArrowRight size={13} strokeWidth={2} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      <GoogleReviews />
    </main>
  );
}
